import { type FC, type MouseEvent as ReactMouseEvent } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../core';
import type { IconName } from '../../Icons/Icons';
import { FAIcon } from '../../Icons/Icons';
import Tooltip, { TooltipPreference } from '../../Tooltip/Tooltip';
import { type InputFieldPropsTypes } from '../types/propsType';
import { InputTypes, InputValidations } from '../types/enums';
import { filterIconTheme } from '../../utils/ThemeFilter/ThemeFilter';
import { KeyConstants } from '../../Accordion/types/enums';
import { DynamicObjectInterface } from '../../Table/types/propType';
import styles from './ClassicInput.module.scss';
const cx = classNames.bind(styles);

/**
 * @description Input provide <input></input> with basic css;
 *  styling can be override through prop (className) only.
 *  by default : disabled, readOnly are false;
 */

const ClassicInput: FC<InputFieldPropsTypes> = ({
  id = '',
  name = '',
  label = '',
  message = '',
  className = '',
  labelInfo = '',
  labelInfoPreference = TooltipPreference.TOP_CENTER,
  disabled = false,
  readOnly = false,
  required = false,
  showAsteriskForMandatoryField = false,
  status,
  onClear,
  inputIcon,
  forwardedRef,
  iconClassName,
  inputWithDropdown,
  isSensitiveField = true,
  ...restProps
}) => {
  const inputType = restProps.type === InputTypes.NUMBER ? InputTypes.TEXT : restProps.type;

  const { DEFAULT, VALID, INVALID } = InputValidations;
  const configs = ConfigService.getConfigs();
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;

  let currentIcon = {};
  if (!inputWithDropdown) {
    if (inputIcon) {
      currentIcon = inputIcon;
    } else if (status === VALID) {
      currentIcon = { iconName: 'circle-check' as IconName };
    } else if (status === INVALID) {
      currentIcon = { iconName: 'square-exclamation' as IconName };
    } else if (onClear) {
      currentIcon = { iconName: 'x-mark' as IconName };
    }
  }

  const onClick = (e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (onClear) {
      onClear(e);
    }
  };

  const CInputClassname = cx(
    'input',
    {
      'input-validated-valid': status && status !== DEFAULT && status === VALID,
      'input-validated-invalid': status && status !== DEFAULT && status !== VALID,
      'input-icon-color': inputIcon,
    },
    'input-default-behaviour',
  );

  const CInputLabelClassname = cx('input-label-text', { 'show-asterisk': required && showAsteriskForMandatoryField });

  const keys: string[] = [KeyConstants.ENTER, KeyConstants.SPACE];
  const shouldHandleKeyDown = (e: DynamicObjectInterface) => {
    if (keys.includes(e.code)) {
      onClear?.(e);
    }
  };

  return (
    <div id={id} className={CInputClassname}>
      {label && (
        <label htmlFor={name} className={styles['input-label']}>
          <span className={CInputLabelClassname} data-testid="input-label-text-test">
            {label}
          </span>
          {labelInfo && (
            <Tooltip message={labelInfo} preference={labelInfoPreference} className={styles['input-tool-tip']} />
          )}
        </label>
      )}
      <div
        className={`${styles['input-field-container']} ${disabled ? styles['disabled'] : ''} ${
          readOnly ? styles['read-only'] : ''
        }`}
      >
        <input
          className={`${styles['input-field']} ${className}`}
          data-testid="input-test-id"
          ref={forwardedRef}
          id={name}
          name={name}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          {...restProps}
          type={inputType}
          data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
        />
        <FAIcon
          tabIndex={inputIcon ? 0 : undefined}
          {...optionalProp('theme', filterIconTheme(restProps.theme))}
          data-testid="input-icon-test-id"
          className={`${styles['input-icon']} ${styles['input-box-icon']}  ${iconClassName || ''}`}
          onClick={onClick}
          onKeyDown={shouldHandleKeyDown}
          {...currentIcon}
        />
      </div>
      {message && (
        <span
          role="presentation"
          aria-label="message"
          aria-details={`${label} input message`}
          className={styles['input-message']}
        >
          {message}
        </span>
      )}
    </div>
  );
};
ClassicInput.displayName = 'ClassicInput';
export default ClassicInput;
